import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://localhost:7001/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 seconds — Start My Day can take 10+ seconds on first sync
});

// Token is injected by the auth interceptor set up in App.tsx
// This allows access to the React auth context from outside components
let tokenGetter: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  tokenGetter = getter;
}

// Principal context — when an EA selects a principal, this ID is appended
// to all API requests as forExecutiveUserId. Set by PrincipalContext.
let _activePrincipalId: string | null = null;

export function setActivePrincipalId(id: string | null) {
  _activePrincipalId = id;
}

apiClient.interceptors.request.use(async (config) => {
  // Auth token injection
  if (tokenGetter) {
    try {
      const token = await tokenGetter();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token expired — will get 401, handled by response interceptor
    }
  }

  // Principal context injection — append forExecutiveUserId to all requests
  // when an EA has selected a principal. Does NOT override explicit params.
  if (_activePrincipalId && !config.params?.forExecutiveUserId) {
    config.params = { ...config.params, forExecutiveUserId: _activePrincipalId };
  }

  return config;
});

// Response interceptor — catches the backend's MFA-required wall.
// `RequireMfaCompleteMiddleware` returns 403 with body
// `{ error: "mfa_setup_required", message: ... }` for admin-class users
// whose token carries `mfa_pending=true` AND who hit a non-allowed
// endpoint (everything except /api/auth/mfa/*, /auth/refresh, /auth/me,
// /auth/logout). We catch the 403 globally and redirect to /mfa-setup
// so the user can't get stuck on a page that 403s without any cue —
// the redirect IS the cue.
//
// Idempotent: if we're already on /mfa-setup don't redirect again
// (would loop on the setup page's own GET /me call). The setup page's
// allowed endpoints don't return this error, but the guard is cheap.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.error;
    if (status === 403 && code === 'mfa_setup_required') {
      if (typeof window !== 'undefined'
          && window.location.pathname !== '/mfa-setup') {
        window.location.replace('/mfa-setup');
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
